import React, { useEffect, useState } from "react";
import "./TransactionItem.scss";
import locationIcon from "@assets/icons/location-marker.svg";
import contactIcon from "@assets/icons/contact.svg";
import calendarIcon from "@assets/icons/calendar.svg";
import clockIcon from "@assets/icons/clock.svg";
import truckIcon from "@assets/icons/truck.svg";
import { DownOutlined, StarFilled } from "@ant-design/icons";
import {
  Button,
  Image,
  Dropdown,
  Steps,
  StepsProps,
  Typography,
  Checkbox,
} from "antd";
import { ReactComponent as OrderIcon } from "@assets/icons/orderStep.svg";
import { ReactComponent as PlacedIcon } from "@assets/icons/placedStep.svg";
import { ReactComponent as ShippingIcon } from "@assets/icons/shippingStep.svg";
import { ReactComponent as ConfirmIcon } from "@assets/icons/confirmStep.svg";
import { ReactComponent as CompleteIcon } from "@assets/icons/completeStep.svg";
import { ReactComponent as DownFilled } from "@assets/icons/arrow-down-filled.svg";
import { useUnit } from "contexts/UnitContext";
import Helpers from "@utils/Helpers";
import { useNavigate } from "react-router-dom";
import { ProductApi } from "services/ProductApi";
import { usePusher } from "contexts/PusherContext";
import { useUser } from "contexts/UserContext";

interface CustomStyle extends React.CSSProperties {
  "--main-color"?: string;
  "--sub-color"?: string;
  "--img-color"?: string;
  "--border-color"?: string;
}

interface TransactionItemProps {
  data: any;
  type: string;
}

const steps = [
  "Order Placed",
  "Confirmation",
  "Processing",
  "Shipping & Delivery",
  "Completion",
];

const confirmationSelect: any = [
  {
    key: "2",
    label: "Pending",
  },
  {
    key: "3",
    label: "Confirmed",
  },
];

const processingSelect: any = [
  {
    key: "4",
    label: "Processing",
  },
  {
    key: "5",
    label: "Ready to Pickup",
  },
  {
    key: "6",
    label: "Picked",
  },
];

const shippingSelect: any = [
  {
    key: "7",
    label: "Shipping",
  },
  {
    key: "8",
    label: "Arrived",
  },
];

const TransactionItem: React.FC<TransactionItemProps> = ({ data, type }) => {
  const [isOpenProductList, setIsOpenProductList] = useState(false);
  const [isCompleteTransaction, setIsCompleteTransaction] = useState(false);
  const { setIsOpenChatbox, setSelectedReceiver, selectedReceiver } = useUser();
  const { units, convertCurrency } = useUnit();
  const [current, setCurrent] = useState(data.transaction_status.stage - 1);
  const navigate = useNavigate();
  const [labels, setLabels] = useState<string[]>([]);
  const { message, setMessage } = usePusher();

  const [currentStage, setCurrentStage] = useState(
    data.transaction_status.stage
  );
  const [currentStatus, setCurrentStatus] = useState(
    data.transaction_status.status
  );
  const [pipelineTime, setPipelineTime] = useState(
    data.transaction_status.time
  );
  const { userProfile } = useUser();

  // HANDLE TRIGGER MESSAGE
  useEffect(() => {
    if (
      message !== null &&
      message.module === "Transaction" &&
      message.message.transaction_id === data.producer_farm.transaction_id &&
      message.message.target === type
    ) {
      setCurrentStage(message.message.current_stage);
      setCurrentStatus(message.message.current_status);
      setPipelineTime(message.message.time);
      setCurrent(message.message.current_stage - 1);
      setMessage(null);
    }
  }, [message]);

  const formatDateTime = (date: string | Date) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const day = dateObj.getDate().toString().padStart(2, "0");

    let hours = dateObj.getHours().toString().padStart(2, "0");
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= "12" ? "PM" : "AM";
    hours = hours >= "12" ? String(Number(hours) - 12) : hours;

    return `${year}-${month}-${day}, ${hours}:${minutes} ${ampm}`;
  };

  const handleSelect = (key: string, value: string) => {
    let payload = {
      transaction_id: data.producer_farm.transaction_id,
      stage_id: key === "confirm" ? 2 : key === "process" ? 3 : 4,
      status_id: Number(value),
    };
    const newPipelineTime = [...pipelineTime];
    switch (key) {
      case "confirm":
        if (value === "3") {
          newPipelineTime[1] = Helpers.getCurrentDateTime();
          setPipelineTime(newPipelineTime);
          setCurrent(2);
          setCurrentStage(3);
          setCurrentStatus(4);
        }
        break;
      case "process":
        if (value !== "6") {
          setCurrentStage(3);
          setCurrentStatus(Number(value));
        } else {
          newPipelineTime[2] = Helpers.getCurrentDateTime();
          setPipelineTime(newPipelineTime);
          setCurrentStage(4);
          setCurrentStatus(7);
          setCurrent(3);
        }
        break;
      case "shipping":
        if (value !== "9") {
          setCurrentStage(4);
          setCurrentStatus(Number(value));
        } else {
          newPipelineTime[3] = Helpers.getCurrentDateTime();
          newPipelineTime[4] = Helpers.getCurrentDateTime();
          setPipelineTime(newPipelineTime);
          setCurrentStage(5);
          setCurrentStatus(9);
          setCurrent(4);
        }
        break;
    }
    updateStatus(payload);
  };

  const updateStatus = async (payload: any, isLastUpdate?: boolean) => {
    try {
      const response = await ProductApi.UpdateStatus(payload);
      if (response.status === 200) {
        if (isLastUpdate) {
          const newPipelineTime = [...pipelineTime];
          newPipelineTime[4] = Helpers.getCurrentDateTime();
          setCurrent(4);
          setPipelineTime(newPipelineTime);
          setCurrentStage(response.data.data.order_item_stage_id);
          setCurrentStatus(response.data.data.order_item_status_id);
          setIsCompleteTransaction(true);
        }
      }
    } catch (error) {
      Helpers.notification.error("Can not update status");
    }
  };

  const customDot: StepsProps["progressDot"] = (dot, { status, index }) => {
    return (
      <div className={`transaction-step-dot ${status}`}>
        {index === 0 && <PlacedIcon />}
        {index === 1 && <ConfirmIcon />}
        {index === 2 && <OrderIcon />}
        {index === 3 && <ShippingIcon />}
        {index === 4 && <CompleteIcon />}
      </div>
    );
  };

  const orderItemPrice = () => {
    let total: any = 0;
    data.product_items.forEach((item: any, index: number) => {
      const pricePerItem: number =
        item.quantity *
        (item.bale_weight / 1000) *
        convertCurrency(item.price_per_tons, "USD", units.currency);
      total += pricePerItem;
    });
    return Helpers.formatNumber(total, 2);
  };

  useEffect(() => {
    if (currentStage === 2) {
      const confirmLabel =
        confirmationSelect.find(
          (item: any) => item?.key === currentStatus.toString()
        ).label || "Unknown";
      setLabels([confirmLabel, "", ""]);
    }

    if (currentStage === 3) {
      const confirmLabel = pipelineTime[1]
        ? Helpers.formatDateTime(pipelineTime[1])
        : "--/--/---- --:--";
      const processLabel =
        processingSelect.find(
          (item: any) => item?.key === currentStatus.toString()
        ).label || "Unknown";
      setLabels([confirmLabel, processLabel, ""]);
    }

    if (currentStage === 4) {
      const confirmLabel = pipelineTime[1]
        ? Helpers.formatDateTime(pipelineTime[1])
        : "--/--/---- --:--";
      const processLabel = pipelineTime[2]
        ? Helpers.formatDateTime(pipelineTime[2])
        : "--/--/---- --:--";
      const shippingLabel =
        shippingSelect.find(
          (item: any) => item?.key === currentStatus.toString()
        ).label || "Unknown";
      setLabels([confirmLabel, processLabel, shippingLabel]);
    }

    if (currentStage === 5) {
      const confirmLabel = pipelineTime[1]
        ? Helpers.formatDateTime(pipelineTime[1])
        : "--/--/---- --:--";
      const processLabel = pipelineTime[2]
        ? Helpers.formatDateTime(pipelineTime[2])
        : "--/--/---- --:--";
      const shippingLabel = pipelineTime[3]
        ? Helpers.formatDateTime(pipelineTime[3])
        : "--/--/---- --:--";
      const completeLabel = pipelineTime[4]
        ? Helpers.formatDateTime(pipelineTime[4])
        : "--/--/---- --:--";
      setLabels([confirmLabel, processLabel, shippingLabel, completeLabel]);
    }
  }, [currentStage, currentStatus]);

  const handleOpenChatBox = () => {
    if (type === "consumer") {
      setSelectedReceiver({
        ...selectedReceiver,
        receiverId: data?.producer_farm?.producer_id,
        receiverName: data?.producer_farm?.producer_name,
        receiverAvatar: data?.producer_farm?.avatar,
      });
    } else {
      setSelectedReceiver({
        ...selectedReceiver,
        receiverId: data?.consumer_info?.user_id,
        receiverName: data?.consumer_info?.display_name,
        receiverAvatar: data?.consumer_info?.avatar,
      });
    }
    setIsOpenChatbox(true);
  };

  return (
    <div
      className="TransactionItem"
      id={`${data?.producer_farm?.transaction_id}`}
      style={
        {
          "--header-bg": type === "consumer" ? "transparent" : "#EBF3EC",
          "--delivery-bg": type === "consumer" ? "#EBF3EC" : "transparent",
          "--img-color":
            "brightness(0) saturate(100%) invert(41%) sepia(23%) saturate(1085%) hue-rotate(76deg) brightness(98%) contrast(89%)",
          "--border-color": "#C1D9C4",
        } as CustomStyle
      }
    >
      <div className="transaction-header">
        <div className="header-info">
          <div className="farm-info">
            {type === "producer" && <Checkbox />}
            <div className="farm-info-group">
              <div className="farm-name">
                {type === "consumer"
                  ? data.producer_farm.producer_name
                  : data.consumer_info.display_name}{" "}
                <span className="rating">
                  <StarFilled /> 4.5
                </span>
              </div>
              <div className="farm-location">
                <img src={locationIcon} width={15} alt="location" />
                <span>{data?.producer_farm?.province}</span>
              </div>
            </div>
          </div>
          <div className="total-price">
            Total:
            <br />${orderItemPrice()}
          </div>
          <div className="ship-to">
            Ship to:
            <br />{" "}
            {type === "consumer"
              ? `${userProfile?.first_name} ${userProfile?.last_name}`
              : `${data.consumer_info.first_name} ${data.consumer_info.last_name}`}
          </div>
          <div className="order-id">
            Order ID:
            <br /> {data.order.order_id}
          </div>
          <Button
            type="link"
            className="contact-btn"
            onClick={handleOpenChatBox}
          >
            {<img src={contactIcon} alt="contact" />}{" "}
            {type === "producer" ? "Contact Consumer" : "Contact Producer"}
          </Button>
        </div>
      </div>
      <div className="transaction-delivery">
        <div className="delivery-info">
          <div className="shipping-mode">
            <img src={truckIcon} width={17} alt="truck" />
            <span>
              {data.producer_farm?.shipping_method?.replace(
                /^./,
                data.producer_farm.shipping_method[0].toUpperCase()
              )}
            </span>
          </div>
          <div className="shipping-date">
            <img src={calendarIcon} width={17} alt="calendar" />
            <span>Dec 12 - Dec 16</span>
          </div>
          <div className="shipping-distance">
            <img src={locationIcon} width={17} alt="location" />
            <span>{data?.producer_farm?.distance.toFixed(0)} km</span>
          </div>
          <div className="shipping-time">
            <img src={clockIcon} width={17} alt="clock" />
            <span>3h 22m</span>
          </div>
        </div>
      </div>
      <div className="transaction-step">
        {type === "consumer" ? (
          <Steps
            current={
              currentStage == 2 && currentStatus == 2
                ? current - 1
                : currentStage == 3 && currentStatus == 4
                ? current - 1
                : current
            }
            progressDot={customDot}
            items={steps.map((step, index) => ({
              title: step,
              description:
                index !== 0
                  ? currentStage > index + 1
                    ? formatDateTime(pipelineTime[index] || "--/--/----, --:--")
                    : currentStage === index + 1
                    ? currentStage !== 5
                      ? (currentStage === 2
                          ? confirmationSelect
                          : currentStage === 3
                          ? processingSelect
                          : shippingSelect
                        ).find(
                          (item: any) => item.key === currentStatus.toString()
                        )?.label || "undefined"
                      : formatDateTime(pipelineTime[4])
                    : ""
                  : formatDateTime(pipelineTime[0]),
            }))}
          />
        ) : (
          <Steps
            current={current}
            progressDot={customDot}
            items={[
              {
                title: "Order Placed",
                description: formatDateTime(pipelineTime[0]),
              },
              {
                title: "Confirmation",
                description:
                  current === 1 ? (
                    <Dropdown
                      menu={{
                        items: confirmationSelect,
                        selectable: true,
                        selectedKeys: [
                          currentStage === 2 ? currentStatus.toString() : "",
                        ],
                        onSelect: ({ key }) => {
                          handleSelect("confirm", key);
                        },
                      }}
                      trigger={["click"]}
                    >
                      <Typography.Link
                        style={{ width: "max-content" }}
                        className="status-dropdown"
                      >
                        {labels[0]}
                        <DownFilled />
                      </Typography.Link>
                    </Dropdown>
                  ) : (
                    <Typography.Link
                      style={{ width: "max-content" }}
                      className="status-dropdown"
                    >
                      {labels[0]}
                    </Typography.Link>
                  ),
              },
              {
                title: "Processing",
                description:
                  current === 2 ? (
                    <Dropdown
                      menu={{
                        items: processingSelect,
                        selectable: true,
                        selectedKeys: [
                          currentStage === 3 ? currentStatus.toString() : "",
                        ],
                        onSelect: ({ key }) => handleSelect("process", key),
                      }}
                      trigger={["click"]}
                    >
                      <Typography.Link
                        style={{ width: "max-content" }}
                        className="status-dropdown"
                      >
                        {labels[1]}
                        <DownFilled />
                      </Typography.Link>
                    </Dropdown>
                  ) : (
                    <Typography.Link
                      style={{ width: "max-content" }}
                      className="status-dropdown"
                    >
                      {labels[1]}
                    </Typography.Link>
                  ),
              },
              {
                title: "Shipping & Delivery",
                description:
                  current === 3 ? (
                    <Dropdown
                      menu={{
                        items: shippingSelect,
                        selectable: true,
                        selectedKeys: [
                          currentStage === 4 ? currentStatus.toString() : "",
                        ],
                        onSelect: ({ key }) => handleSelect("shipping", key),
                      }}
                      trigger={["click"]}
                    >
                      <Typography.Link
                        style={{ width: "max-content" }}
                        className="status-dropdown"
                      >
                        {labels[2]}
                        <DownFilled />
                      </Typography.Link>
                    </Dropdown>
                  ) : (
                    <Typography.Link
                      style={{ width: "max-content" }}
                      className="status-dropdown"
                    >
                      {labels[2]}
                    </Typography.Link>
                  ),
              },
              {
                title: "Completion",
                description: (
                  <Typography.Link
                    style={{ width: "max-content" }}
                    className="status-dropdown"
                  >
                    {labels[3]}
                  </Typography.Link>
                ),
              },
            ]}
          />
        )}
      </div>

      {type === "consumer" && currentStage === 4 && currentStatus === 8 && (
        <div
          className={`btn-complete-wrap ${
            isCompleteTransaction && "completed"
          }`}
        >
          <Button
            className="complete-btn"
            type="link"
            onClick={() => {
              const payload = {
                transaction_id: data.producer_farm.transaction_id,
                stage_id: 5,
                status_id: 9,
              };
              updateStatus(payload, true);
            }}
          >
            Complete
          </Button>
        </div>
      )}
      <div
        className={`transaction-product ${!isOpenProductList ? "hide" : ""}`}
      >
        {data.product_items.map((item: any, index: number) => {
          const pricePerItem: number =
            item.quantity *
            (item.bale_weight / 1000) *
            convertCurrency(item.price_per_tons, "USD", units.currency);
          return (
            <div className="product-item" key={index}>
              <Image
                width={100}
                className="product-image"
                height={100}
                preview={false}
                src={item.image}
              />
              <div className="product-info">
                <div className="product-name">{item.hay_type}</div>
                <div className="product-quantity">
                  Quantity: <span>{item.quantity} Bales</span>
                </div>
              </div>
              <div className="product-prices">
                <div className="category">Hay</div>
                <div className="per-weight">
                  $
                  {Helpers.formatNumber(
                    convertCurrency(
                      item.unit == "Ton"
                        ? item.price_per_tons
                        : item.price_per_bale,
                      "USD",
                      units.currency
                    ),
                    2
                  )}{" "}
                  /{item.unit}
                </div>
                <div className="total">
                  ${Helpers.formatNumber(pricePerItem, 2)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="transaction-control">
        <Button
          type="link"
          onClick={() => setIsOpenProductList(!isOpenProductList)}
          className="transaction-control-btn product-btn"
        >
          View Products <DownOutlined />
        </Button>
        <Button type="link" className="transaction-control-btn review-btn">
          Write Review
        </Button>
        <Button
          type="link"
          onClick={() => {
            const encodedResponse = encodeURIComponent(JSON.stringify(data));
            console.log(data);

            navigate("detail?data=" + encodedResponse);
          }}
          className="transaction-control-btn detail-btn"
        >
          Order Details
        </Button>
      </div>
    </div>
  );
};

export default TransactionItem;
